import { createVisitorNameDisplay } from '../ui/visitorNameDisplay';
import type { ControlState } from '../controls/types';
import { createVisitorAvatars } from '../multiplayer/avatars';
import { applyPoseToCamera } from './cameraRig';
import type { WorldInit } from './initializeWorld';
import { createVisitorView } from './visitorView';
import type { components } from '../multiplayer/api.generated';
import type { TourSnapshot } from '../tours/types';
import { tourBodyPose } from '../tours/tourVisitor';

export function createVisitorRuntime(
  init: WorldInit,
  profile: () => Pick<
    components['schemas']['Visitor'],
    'character' | 'name' | 'clothing' | 'emote'
  >,
  epochMs: () => number = Date.now
) {
  const view = createVisitorView(document);
  const names = createVisitorNameDisplay(document);
  const avatar = createVisitorAvatars(init, 1, 'local-visitor', () => names.visible);
  let before: readonly number[] | null = null;
  let walked = 0;
  return {
    names,
    toggleView: view.toggle,
    lights: avatar.lights,
    diagnostics: () => avatar.diagnostics(),
    get behind(): boolean {
      return view.behind;
    },
    prepareInput: (...args: Parameters<typeof view.prepareInput>) => view.prepareInput(...args),
    mount(): void {
      names.mount();
    },
    update(
      control: ControlState,
      seconds: number,
      dt: number,
      night = 0,
      tour: TourSnapshot | null = null
    ): void {
      const { pose, mode, atPainterView } = control;
      const body = tourBodyPose(pose, tour);
      const travelling = tour?.kind === 'boat' ? 'boat' : mode;
      const carrying = travelling === 'walk' || travelling === 'boat';
      const distance = before
        ? Math.hypot(pose.position[0] - (before[0] ?? 0), pose.position[2] - (before[2] ?? 0))
        : 0;
      const velocity: readonly [number, number] =
        before && dt > 0 && mode === 'walk'
          ? [(pose.position[0] - before[0]!) / dt, (pose.position[2] - before[2]!) / dt]
          : [0, 0];
      before = pose.position;
      walked += distance;
      const visible = view.behind && !atPainterView;
      if (visible)
        applyPoseToCamera(
          view.cameraPose(pose, init.env.collision),
          init.camera,
          init.env.groundHeightAt([pose.position[0], -pose.position[2]]),
          1
        );
      avatar.update(
        !atPainterView && (visible || carrying)
          ? [
              {
                id: 'local',
                seq: 0,
                speech: null,
                ...profile(),
                x: body.position[0],
                y: body.position[1],
                z: body.position[2],
                yaw: body.yaw,
                mode: travelling === 'boat' ? 'boat' : 'walk',
                walked,
                gait: mode === 'walk' && distance > 0.001 ? 1 : 0,
                velocity,
              },
            ]
          : [],
        seconds,
        carrying ? night : 0,
        visible,
        epochMs()
      );
    },
    dispose(): void {
      view.dispose();
      names.dispose();
      avatar.dispose();
    },
  };
}
